import { type FC, useState, useEffect, useRef } from "react";
import api from "../services/api";
import { useData } from "../context/DataContext";
import { getCategoryBadge } from "../utils/utils";
import type { Page, CategoryType, OneShotGroupDto, QrCode } from "../types";
import QrCodeListDisplayModal from "../components/QrCodeListDisplayModal";
import ActionMenu from "../components/ActionMenu";
import { useNavigate } from "react-router-dom";

// --- Icons ---
const Icons = {
    Group: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
    Qr: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4h2v-4zM6 8V4h6v4H6zm14 10a2 2 0 11-4 0 2 2 0 014 0zM6 20h6v-4H6v4z" /></svg>,
    Trash: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
};

const OneShotGroupsPage: FC = () => {
    const [oneShotGroupsPage, setOneShotGroupsPage] = useState<Page<OneShotGroupDto> | null>(null);
    const [activeCategory, setActiveCategory] = useState<number>(-1);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const navigate = useNavigate();

    // Stato per il modale della lista di QR code
    const [qrCodesForModal, setQrCodesForModal] = useState<QrCode[] | null>(null);
    const [isFetchingGroupDetails, setIsFetchingGroupDetails] = useState(false);

    // Stato per l'eliminazione
    const [deletingGroupId, setDeletingGroupId] = useState<number | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<OneShotGroupDto | null>(null);

    const { categories } = useData();
    const successMessageTimeoutRef = useRef<number | null>(null);

    const fetchGroupsData = async (page: number, categoryId: number) => {
        setIsLoading(true);
        setError(null);
        try {
            const cacheBuster = Date.now();
            const response = await api.get('/api/oneshot/getAll', {
                params: { page, size: 20, idCategory: categoryId, _: cacheBuster },
            });
            if (response.data.status) {
                setOneShotGroupsPage(response.data.data);
            } else {
                throw new Error("Impossibile caricare l'elenco dei gruppi.");
            }
        } catch (err: any) {
            setError(err.message || "Si è verificato un errore di rete.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewGroup = async (group: OneShotGroupDto) => {
        setIsFetchingGroupDetails(true);
        setError(null);
        try {
            const response = await api.get(`/api/qrcodes/getByOneShot/${group.id}`);
            if (response.data.status) {
                setQrCodesForModal(response.data.data);
            } else {
                throw new Error("Impossibile caricare i QR code del gruppo.");
            }
        } catch (err: any) {
            setError(err.message || "Si è verificato un errore durante il recupero dei dettagli del gruppo.");
        } finally {
            setIsFetchingGroupDetails(false);
        }
    };

    const handleDeleteGroup = async (group: OneShotGroupDto) => {
        setShowDeleteModal(false);
        setDeletingGroupId(group.id);
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await api.delete(`/api/oneshot/delete/${group.id}`);
            if (response.data.status) {
                setSuccessMessage("Gruppo eliminato con successo!");
                fetchGroupsData(currentPage, activeCategory);
            } else {
                throw new Error(response.data.message || "Impossibile eliminare il gruppo.");
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || "Si è verificato un errore durante l'eliminazione.");
        } finally {
            setDeletingGroupId(null);
            if (successMessageTimeoutRef.current) {
                clearTimeout(successMessageTimeoutRef.current);
            }
            successMessageTimeoutRef.current = window.setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
        }
    };

    const handleCloseModal = () => {
        setQrCodesForModal(null);
    };

    const confirmDelete = (group: OneShotGroupDto) => {
        setGroupToDelete(group);
        setShowDeleteModal(true);
    };

    const cancelDelete = () => {
        setGroupToDelete(null);
        setShowDeleteModal(false);
    };

    useEffect(() => {
        fetchGroupsData(currentPage, activeCategory);
    }, [currentPage, activeCategory]);

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentPage(0);
        setActiveCategory(Number(e.target.value));
    };

    if (isLoading && !oneShotGroupsPage) {
        return (
            <div className="animate-pulse space-y-4 mt-8">
                <div className="h-10 bg-slate-800/60 rounded-xl w-1/4 mb-6"></div>
                <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
                    <div className="h-16 bg-slate-800/50 border-b border-slate-700/50"></div>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex justify-between items-center p-5 border-b border-slate-700/50">
                            <div className="h-5 bg-slate-700/50 rounded w-1/4"></div>
                            <div className="h-5 bg-slate-700/50 rounded w-1/6"></div>
                            <div className="h-8 bg-slate-700/50 rounded-lg w-8"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 md:space-y-8 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                        Gruppi One-Shot
                    </h1>
                    <p className="text-slate-400 mt-1 md:mt-2 text-base md:text-lg">Gestione raggruppata di QR Code monouso</p>
                </div>
            </div>

            {/* Notifications */}
            {error && (
                <div className="p-4 bg-red-900/20 border border-red-800/50 backdrop-blur text-red-200 rounded-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>{error}</span>
                </div>
            )}
            {successMessage && (
                <div className="p-4 bg-emerald-900/20 border border-emerald-800/50 backdrop-blur text-emerald-200 rounded-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                    <span>{successMessage}</span>
                </div>
            )}

            {/* Main Content Card */}
            <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-xl overflow-hidden">

                {/* Toolbar */}
                <div className="p-4 md:p-6 border-b border-slate-700/50 flex flex-col md:flex-row justify-between items-center gap-4 bg-slate-800/20">
                    <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-2 self-start md:self-auto">
                        Elenco Gruppi
                    </h2>

                    <div className="relative w-full md:w-64">
                        <select
                            onChange={handleCategoryChange}
                            value={activeCategory}
                            className="w-full bg-slate-900/50 border border-slate-700 text-slate-200 text-sm rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent block p-2.5 pr-10 transition-all hover:bg-slate-900/80 appearance-none"
                        >
                            <option value={-1}>Tutte le categorie</option>
                            {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-500">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </div>
                </div>

                {/* Table Content */}
                {isFetchingGroupDetails ? (
                    <div className="flex flex-col items-center justify-center p-12 text-slate-400">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sky-500 mb-4"></div>
                        <p>Caricamento dettagli gruppo...</p>
                    </div>
                ) : (
                    <div className="min-h-[300px] p-4 md:p-0">
                        <table className="w-full text-left block md:table border-collapse">
                            {/* Header: Nascosto su mobile */}
                            <thead className="hidden md:table-header-group bg-slate-900/60 text-xs uppercase text-slate-400 font-semibold tracking-wider">
                            <tr>
                                <th className="p-5 border-b border-slate-700/50">Nome</th>
                                <th className="p-5 border-b border-slate-700/50">Categoria</th>
                                <th className="p-5 border-b border-slate-700/50">Cliente</th>
                                <th className="p-5 border-b border-slate-700/50 text-center">Quantità</th>
                                <th className="p-5 border-b border-slate-700/50 text-center">Scansioni</th>
                                <th className="p-5 border-b border-slate-700/50 text-right">Azioni</th>
                            </tr>
                            </thead>

                            {/* Body */}
                            <tbody className="block md:table-row-group md:divide-y md:divide-slate-700/50">
                            {oneShotGroupsPage?.content.map(group => (
                                <tr
                                    key={group.id}
                                    onClick={() => handleViewGroup(group)}
                                    className="block md:table-row bg-slate-800/40 md:bg-transparent border border-slate-700/50 md:border-none rounded-2xl md:rounded-none mb-4 md:mb-0 hover:bg-slate-700/30 transition-colors group cursor-pointer shadow-lg md:shadow-none"
                                    title="Clicca per visualizzare i QR del gruppo"
                                >
                                    {/* Cella: Nome */}
                                    <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                        <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Nome</span>
                                        <div className="font-semibold text-white flex items-center gap-3 text-right md:text-left">
                                            <div className="hidden md:flex p-2 bg-slate-700/50 rounded-lg text-slate-400 group-hover:text-sky-400 group-hover:bg-sky-400/10 transition-colors">
                                                {Icons.Group}
                                            </div>
                                            <span className="truncate max-w-[150px] sm:max-w-[250px] md:max-w-none">{group.name}</span>
                                        </div>
                                    </td>

                                    {/* Cella: Categoria */}
                                    <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                        <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Categoria</span>
                                        <div className="text-right md:text-left">{getCategoryBadge(group.category as CategoryType)}</div>
                                    </td>

                                    {/* Cella: Cliente */}
                                    <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                        <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Cliente</span>
                                        <div className="text-slate-300 text-sm text-right md:text-left">{group.client || <span className="text-slate-600 italic">N/A</span>}</div>
                                    </td>

                                    {/* Cella: Quantità */}
                                    <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                        <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Quantità</span>
                                        <div className="text-right md:text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                                                {group.count}
                                            </span>
                                        </div>
                                    </td>

                                    {/* Cella: Scansioni */}
                                    <td className="flex justify-between items-center md:table-cell p-4 md:p-5 border-b border-slate-700/30 md:border-b md:border-slate-700/50 last:border-0">
                                        <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Scansioni</span>
                                        <div className="text-right md:text-center">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${group.scanned > 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-700/50 text-slate-500'}`}>
                                                {group.scanned}
                                            </span>
                                        </div>
                                    </td>

                                    {/* Cella: Azioni */}
                                    <td
                                        className="flex justify-between items-center md:table-cell p-4 md:p-5 text-right bg-slate-900/20 md:bg-transparent rounded-b-2xl md:rounded-none"
                                        onClick={(e) => e.stopPropagation()} /* Impedisce che il click sui bottoni apra la modale */
                                    >
                                        <span className="md:hidden text-xs font-bold text-slate-500 uppercase tracking-wider">Azioni</span>
                                        <div className="flex justify-end">
                                            <ActionMenu
                                                onView={() => handleViewGroup(group)}
                                                onDelete={() => confirmDelete(group)}
                                                isDeleting={deletingGroupId === group.id}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {oneShotGroupsPage?.content.length === 0 && (
                                <tr className="block md:table-row">
                                    <td colSpan={6} className="block md:table-cell p-8 md:p-16 border-none">
                                        <div className="flex flex-col items-center justify-center text-center p-8 rounded-3xl border-2 border-dashed border-slate-700/60 bg-slate-800/20 hover:bg-slate-800/40 transition-colors">
                                            <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mb-4 shadow-inner ring-1 ring-slate-700 text-slate-500">
                                                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                                            </div>
                                            <h3 className="text-xl font-bold text-white mb-2">Nessun dato presente</h3>
                                            <p className="text-slate-400 max-w-sm mb-6">Non ci sono ancora elementi in questa categoria. Prova a cambiare filtro o creane uno nuovo.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {oneShotGroupsPage && oneShotGroupsPage.totalPages > 1 && (
                    <div className="p-4 border-t border-slate-700/50 bg-slate-800/30 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-400">
                        <span>Pagina <span className="text-white font-semibold">{oneShotGroupsPage.number + 1}</span> di {oneShotGroupsPage.totalPages}</span>
                        <div className="flex space-x-2 w-full sm:w-auto">
                            <button
                                onClick={() => setCurrentPage(p => p - 1)}
                                disabled={oneShotGroupsPage.number === 0}
                                className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs uppercase tracking-wide text-center"
                            >
                                Precedente
                            </button>
                            <button
                                onClick={() => setCurrentPage(p => p + 1)}
                                disabled={oneShotGroupsPage.number >= oneShotGroupsPage.totalPages - 1}
                                className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs uppercase tracking-wide text-center"
                            >
                                Successiva
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {qrCodesForModal && (
                <QrCodeListDisplayModal
                    qrCodes={qrCodesForModal}
                    onClose={handleCloseModal}
                    download={true}
                />
            )}

            {showDeleteModal && groupToDelete && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4 z-50 animate-fade-in">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl transform transition-all scale-100">
                        <div className="p-6">
                            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4 text-red-500">
                                {Icons.Trash}
                            </div>
                            <h3 className="text-xl font-bold text-white">Conferma Eliminazione</h3>
                            <p className="text-slate-400 mt-2">
                                Sei sicuro di voler eliminare il gruppo <span className="text-white font-semibold">"{groupToDelete.name}"</span>?
                                <br />Questa azione è irreversibile e cancellerà anche tutti i QR code associati.
                            </p>
                        </div>
                        <div className="p-4 bg-slate-900/50 rounded-b-2xl flex justify-end space-x-3">
                            <button onClick={cancelDelete} className="px-5 py-2.5 rounded-xl text-slate-300 font-medium hover:bg-slate-700 transition-colors">Annulla</button>
                            <button onClick={() => handleDeleteGroup(groupToDelete)} className="px-5 py-2.5 rounded-xl text-white font-semibold bg-red-600 hover:bg-red-500 shadow-lg shadow-red-500/20 transition-all">Elimina</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OneShotGroupsPage;